import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { coursesService } from '../../services/api';

import type {
  AdminCourseSatisfactionStory,
  CourseSatisfactionKpiRow,
} from '../../types';

function normalizeKpiEditorRows(k?: CourseSatisfactionKpiRow[] | null): CourseSatisfactionKpiRow[] {
  const filled = (k ?? []).filter((x) => (x.label ?? '').trim() || (x.valueFa ?? '').trim());
  const out: CourseSatisfactionKpiRow[] = filled.map((x) => ({
    label: (x.label ?? '').trim(),
    valueFa: (x.valueFa ?? '').trim(),
  }));
  while (out.length < 4) out.push({ label: '', valueFa: '' });
  return out.slice(0, 8);
}

function errMessage(e: unknown): string {
  const msg = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof msg === 'string' ? msg : 'خطا';
}

export default function CourseSatisfactionTab({
  courseId,
  satisfactionAvgLabel: initialAvg,
  satisfactionKpis: initialKpis,
  onHeaderSynced,
}: {
  courseId: string;
  satisfactionAvgLabel?: string | null;
  satisfactionKpis?: CourseSatisfactionKpiRow[] | null;
  onHeaderSynced?: (patch: {
    satisfactionAvgLabel?: string | null;
    satisfactionKpis?: CourseSatisfactionKpiRow[] | null;
  }) => void;
}) {
  const [rows, setRows] = useState<AdminCourseSatisfactionStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const [avgLabel, setAvgLabel] = useState('');
  const [kpiRows, setKpiRows] = useState<CourseSatisfactionKpiRow[]>(() => normalizeKpiEditorRows(null));
  const [headerSaving, setHeaderSaving] = useState(false);

  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [ratingStars, setRatingStars] = useState('5');
  const [coverUrl, setCoverUrl] = useState('');
  const [cardText, setCardText] = useState('');
  const [aparatVideoUrl, setAparatVideoUrl] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [published, setPublished] = useState(true);
  const [pendingCoverFile, setPendingCoverFile] = useState<File | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await coursesService.getSatisfactionStoriesAdmin(courseId);
      setRows(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      setError(errMessage(e) || 'خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setAvgLabel((initialAvg ?? '').trim());
    setKpiRows(normalizeKpiEditorRows(initialKpis));
  }, [courseId, initialAvg, initialKpis]);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id.localeCompare(b.id)),
    [rows],
  );

  const resetStoryForm = () => {
    setCreating(false);
    setEditingId(null);
    setFirstName('');
    setLastName('');
    setJobTitle('');
    setRatingStars('5');
    setCoverUrl('');
    setCardText('');
    setAparatVideoUrl('');
    setSortOrder('0');
    setPublished(true);
    setPendingCoverFile(null);
  };

  const startEdit = (r: AdminCourseSatisfactionStory) => {
    setEditingId(r.id);
    setCreating(false);
    setFirstName(r.firstName);
    setLastName(r.lastName);
    setJobTitle(r.jobTitle ?? '');
    setRatingStars(String(r.ratingStars ?? 5));
    setCoverUrl(r.coverUrl ?? '');
    setCardText(r.cardText);
    setAparatVideoUrl(r.aparatVideoUrl ?? '');
    setSortOrder(String(r.sortOrder ?? 0));
    setPublished(r.published);
    setPendingCoverFile(null);
  };

  const saveHeader = async () => {
    setHeaderSaving(true);
    setError('');
    try {
      const payloadKpis = kpiRows
        .map((r) => ({ label: r.label.trim(), valueFa: r.valueFa.trim() }))
        .filter((r) => r.label && r.valueFa);
      const updated = await coursesService.update(courseId, {
        satisfactionAvgLabel: avgLabel.trim() || null,
        satisfactionKpis: payloadKpis.length ? payloadKpis : null,
      });
      onHeaderSynced?.({
        satisfactionAvgLabel: updated.satisfactionAvgLabel ?? null,
        satisfactionKpis: (updated.satisfactionKpis as CourseSatisfactionKpiRow[] | null) ?? null,
      });
    } catch (e: unknown) {
      setError(errMessage(e));
    } finally {
      setHeaderSaving(false);
    }
  };

  const submitStory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !cardText.trim()) {
      setError('نام، نام خانوادگی و متن کارت الزامی است');
      return;
    }
    const rating = Math.min(5, Math.max(1, Number.parseInt(ratingStars, 10) || 5));
    setBusyId(editingId || '_new');
    setError('');
    try {
      if (editingId) {
        await coursesService.patchSatisfactionStory(courseId, editingId, {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          jobTitle: jobTitle.trim(),
          ratingStars: rating,
          coverUrl: coverUrl.trim() || '',
          cardText: cardText.trim(),
          aparatVideoUrl: aparatVideoUrl.trim(),
          sortOrder: Number.parseInt(sortOrder, 10) || 0,
          published,
        });
        if (pendingCoverFile) {
          await coursesService.uploadSatisfactionStoryCover(courseId, editingId, pendingCoverFile);
        }
      } else {
        const created = await coursesService.createSatisfactionStory(courseId, {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          jobTitle: jobTitle.trim(),
          ratingStars: rating,
          coverUrl: coverUrl.trim() || undefined,
          cardText: cardText.trim(),
          aparatVideoUrl: aparatVideoUrl.trim(),
          sortOrder: Number.parseInt(sortOrder, 10) || 0,
          published,
        });
        if (pendingCoverFile) {
          await coursesService.uploadSatisfactionStoryCover(courseId, created.id, pendingCoverFile);
        }
      }
      resetStoryForm();
      await load();
    } catch (err: unknown) {
      setError(errMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async (id: string) => {
    if (!window.confirm('این مورد حذف شود؟')) return;
    setBusyId(id);
    setError('');
    try {
      await coursesService.deleteSatisfactionStory(courseId, id);
      if (editingId === id) resetStoryForm();
      await load();
    } catch (err: unknown) {
      setError(errMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  if (loading && rows.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-600">
        در حال بارگذاری…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</div>
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <h4 className="text-sm font-bold text-slate-800">بالای سکشن رضایت (میانگین و کارت‌های آماری)</h4>
        <p className="text-xs text-slate-600">
          برچسب میانگین مثل «۴٫۹/۵»؛ تا چهار کارت درصد پیشنهاد می‌شود. برای ذخیره روی دوره کلیک کنید؛ دکمه «ذخیره هدر»
          همین بخش را بدون انتظار برای دکمه پایینی مودال به‌روز می‌کند.
        </p>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">برچسب میانگین رضایت</label>
          <input
            type="text"
            value={avgLabel}
            onChange={(e) => setAvgLabel(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm max-w-md"
            placeholder="مثال: ۴٫۹/۵"
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {kpiRows.slice(0, 4).map((row, idx) => (
            <div key={idx} className="rounded-lg border border-slate-100 p-2 space-y-1.5">
              <span className="text-[11px] font-semibold text-slate-500">کارت {idx + 1}</span>
              <input
                type="text"
                value={row.label}
                onChange={(e) => {
                  const next = [...kpiRows];
                  next[idx] = { ...next[idx], label: e.target.value };
                  setKpiRows(next);
                }}
                placeholder="عنوان (مثل رضایت کلی)"
                className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
              <input
                type="text"
                value={row.valueFa}
                onChange={(e) => {
                  const next = [...kpiRows];
                  next[idx] = { ...next[idx], valueFa: e.target.value };
                  setKpiRows(next);
                }}
                placeholder="مقدار (مثل ۹۴٪)"
                className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </div>
          ))}
        </div>
        <button
          type="button"
          disabled={headerSaving}
          onClick={() => void saveHeader()}
          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {headerSaving ? 'در حال ذخیره…' : 'ذخیره هدر روی دوره'}
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-bold text-slate-800">
            {creating ? 'کارت جدید' : editingId ? 'ویرایش کارت' : 'افزودن کارت شرکت‌کننده'}
          </h4>
          {!creating && !editingId ? (
            <button
              type="button"
              onClick={() => {
                resetStoryForm();
                setCreating(true);
              }}
              className="rounded-lg bg-[#1C3B9B] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#15307d]"
            >
              + کارت جدید
            </button>
          ) : (
            <button
              type="button"
              onClick={resetStoryForm}
              className="text-xs font-medium text-slate-600 hover:text-slate-900"
            >
              انصراف
            </button>
          )}
        </div>

        {(creating || editingId) && (
          <form onSubmit={(e) => void submitStory(e)} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">نام</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">نام خانوادگی</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">شغل / نقش</label>
                <input
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">ستاره (۱ تا ۵)</label>
                <input
                  type="number"
                  min={1}
                  max={5}
                  value={ratingStars}
                  onChange={(e) => setRatingStars(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">متن کارت</label>
              <textarea
                value={cardText}
                onChange={(e) => setCardText(e.target.value)}
                rows={4}
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">لینک ویدیو آپارات</label>
              <input
                type="url"
                value={aparatVideoUrl}
                onChange={(e) => setAparatVideoUrl(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                placeholder="https://www.aparat.com/v/..."
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">آدرس تصویر کاور (اختیاری اگر آپلود نمی‌کنید)</label>
              <input
                type="url"
                value={coverUrl}
                onChange={(e) => setCoverUrl(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">آپلود تصویر کاور</label>
              <p className="mb-2 rounded-lg border border-violet-100 bg-violet-50/80 px-3 py-2 text-[11px] leading-relaxed text-slate-700">
                <span className="font-bold text-violet-900">ابعاد پیشنهادی: </span>
                ۱۰۸۰×۱۹۲۰ پیکسل (نسبت ۹:۱۶، مثل استوری اینستاگرام)
              </p>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={(e) => setPendingCoverFile(e.target.files?.[0] ?? null)}
                className="block w-full text-xs"
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">ترتیب</label>
                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 mt-6 sm:mt-8">
                <input
                  type="checkbox"
                  checked={published}
                  onChange={(e) => setPublished(e.target.checked)}
                />
                منتشر شده
              </label>
            </div>
            <button
              type="submit"
              disabled={busyId !== null}
              className="rounded-lg bg-[#1C3B9B] px-4 py-2 text-sm font-semibold text-white hover:bg-[#15307d] disabled:opacity-50"
            >
              {busyId ? 'در حال ذخیره…' : editingId ? 'به‌روزرسانی کارت' : 'ثبت کارت'}
            </button>
          </form>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h4 className="mb-3 text-sm font-bold text-slate-800">کارت‌های ثبت‌شده ({sorted.length})</h4>
        <div className="space-y-2">
          {sorted.map((r) => (
            <div
              key={r.id}
              className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm"
            >
              <div className="min-w-0 flex-1 space-y-0.5">
                <div className="font-bold text-slate-900">
                  {r.firstName} {r.lastName}
                  {!r.published ? (
                    <span className="mr-2 text-[11px] font-normal text-amber-700">(پیش‌نویس)</span>
                  ) : null}
                </div>
                <div className="text-xs text-slate-600">{r.jobTitle ?? '—'}</div>
                <div className="text-[11px] text-amber-600">★ {r.ratingStars}/۵</div>
                <div className="line-clamp-2 text-xs text-slate-600">{r.cardText}</div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  disabled={busyId !== null}
                  onClick={() => startEdit(r)}
                  className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                >
                  ویرایش
                </button>
                <button
                  type="button"
                  disabled={busyId !== null}
                  onClick={() => void doDelete(r.id)}
                  className="rounded border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                >
                  حذف
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
